import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Drawer,
  Empty,
  Icon,
  Input,
  Modal,
  Spin,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import moment from "moment";
import { Link } from "react-router-dom";
import { createData, getData, patchData } from "../../util/API";
import { copyTextToClipboard } from "../../util/copyToClipboard";
import DocumentPreviewViewer from "./DocumentPreviewViewer";
import {
  DOCUMENT_MAPPING_ROLES,
  POLL_MS,
  REMINDER_MS,
  SNOOZE_STORAGE_KEY,
} from "./constants";
import "./PostVisitDocNotifications.less";

const { Text, Title } = Typography;

function readSnoozeUntil() {
  const raw = localStorage.getItem(SNOOZE_STORAGE_KEY);
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : 0;
}

function snoozeReminder() {
  localStorage.setItem(SNOOZE_STORAGE_KEY, String(Date.now() + REMINDER_MS));
}

function resetMappingState(setters) {
  setters.setDetail(null);
  setters.setAssignments({});
  setters.setSuggestedAssignments({});
  setters.setSpecialData({});
  setters.setPreviewFile(null);
  setters.setDragFileId(null);
}

function PostVisitDocMappingModal({ visible, notificationId, onClose, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [assignments, setAssignments] = useState({});
  const [suggestedAssignments, setSuggestedAssignments] = useState({});
  const [specialData, setSpecialData] = useState({});
  const [previewFile, setPreviewFile] = useState(null);
  const [dragFileId, setDragFileId] = useState(null);

  const loadDetail = useCallback(async () => {
    if (!notificationId) return;
    setLoading(true);
    setLoadError(null);
    try {
      const res = await getData(
        `/management/post-visit-doc-notifications/${notificationId}/detail/`
      );
      if (!res || res.data?.status !== 200 || !Array.isArray(res.data.pending_files)) {
        setLoadError(res?.data?.message || "Could not load document details");
        return;
      }
      setDetail(res.data);
      const sd = { ...(res.data.special_data_current || {}) };
      const suggestions = res.data.ocr_suggestions || {};
      Object.keys(suggestions).forEach((key) => {
        if (suggestions[key] && !sd[key]?.length) {
          sd[key] = [suggestions[key]];
        }
      });
      const flatSd = {};
      Object.keys(sd).forEach((k) => {
        flatSd[k] = Array.isArray(sd[k]) ? sd[k][0] || "" : sd[k] || "";
      });
      setSpecialData(flatSd);
      const fileSuggestions = res.data.file_mapping_suggestions || {};
      const initialAssignments = {};
      Object.keys(fileSuggestions).forEach((fileId) => {
        initialAssignments[Number(fileId)] = fileSuggestions[fileId];
      });
      setSuggestedAssignments(initialAssignments);
      setAssignments(initialAssignments);
      setPreviewFile((res.data.pending_files || [])[0] || null);
    } catch (error) {
      setLoadError("Failed to load mapping details");
      message.error("Failed to load mapping details");
    } finally {
      setLoading(false);
    }
  }, [notificationId]);

  useEffect(() => {
    if (visible && notificationId) {
      resetMappingState({
        setDetail,
        setAssignments,
        setSuggestedAssignments,
        setSpecialData,
        setPreviewFile,
        setDragFileId,
      });
      loadDetail();
    }
  }, [visible, notificationId, loadDetail]);

  const categories = useMemo(() => {
    if (!detail?.document_categories) return [];
    return detail.document_categories.map((c) => c.document__name).filter(Boolean);
  }, [detail]);

  const pendingFiles = detail?.pending_files || [];

  const categoryFiles = useMemo(() => {
    const byCategory = {};
    Object.entries(assignments).forEach(([fileId, category]) => {
      if (!category) return;
      const file = pendingFiles.find((f) => f.id === Number(fileId));
      if (file?.original_name) {
        if (!byCategory[category]) byCategory[category] = [];
        byCategory[category].push({ id: file.id, name: file.original_name });
      }
    });
    return byCategory;
  }, [assignments, pendingFiles]);

  const specialDataKeys = useMemo(() => {
    const serialKeys = detail?.upload_config?.serial_keys || {};
    return Object.values(serialKeys).filter(Boolean);
  }, [detail]);

  const showSpecialData = Boolean(
    detail?.upload_config?.ocr_enabled && specialDataKeys.length > 0
  );

  const ocrSuggestions = detail?.ocr_suggestions || {};

  const assignFile = useCallback((fileId, category) => {
    setAssignments((prev) => ({ ...prev, [fileId]: category }));
  }, []);

  const clearAssignment = useCallback((fileId, event) => {
    if (event) {
      event.stopPropagation();
    }
    setAssignments((prev) => {
      const next = { ...prev };
      delete next[fileId];
      return next;
    });
  }, []);

  const applySerialToField = useCallback((key, value) => {
    if (!key || !value) return;
    setSpecialData((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleSave = useCallback(async () => {
    const pendingIds = new Set(pendingFiles.map((f) => f.id));
    const fileMappings = Object.keys(assignments)
      .filter((fileId) => pendingIds.has(Number(fileId)) && assignments[fileId])
      .map((fileId) => ({
        file_id: Number(fileId),
        document_type: assignments[fileId],
      }));
    if (!fileMappings.length && !showSpecialData) {
      message.info("Assign at least one file to a category");
      return;
    }
    const specialPayload = [];
    if (showSpecialData) {
      specialDataKeys.forEach((key) => {
        if (specialData[key]) {
          specialPayload.push({ keys: key, value: [specialData[key]] });
        }
      });
    }
    setSaving(true);
    try {
      const res = await createData(
        `/management/post-visit-doc-notifications/${notificationId}/map/`,
        { file_mappings: fileMappings, special_data: specialPayload }
      );
      if (res.data?.status === 200) {
        message.success(`Mapped ${res.data.mapped_count} file(s)`);
        onSaved();
        onClose();
      } else {
        message.error(res.data?.message || "Save failed");
      }
    } catch (error) {
      message.error("Save failed");
    } finally {
      setSaving(false);
    }
  }, [
    assignments,
    notificationId,
    onClose,
    onSaved,
    pendingFiles,
    showSpecialData,
    specialData,
    specialDataKeys,
  ]);

  const renderFileThumb = (item) => {
    const ct = (item.content_type || "").toLowerCase();
    const name = (item.original_name || "").toLowerCase();
    if (item.preview_url && (ct.startsWith("image/") || /\.(jpg|jpeg|png|webp)$/.test(name))) {
      return <img src={item.preview_url} alt="" className="pv-map-file-thumb" />;
    }
    return (
      <div className="pv-map-file-thumb-fallback">
        <Icon type={ct.startsWith("video/") ? "video-camera" : "file"} />
      </div>
    );
  };

  const modalTitle = detail?.ticket
    ? `Map documents — ${detail.ticket.cti || detail.ticket.number}`
    : "Map partner documents";

  return (
    <Modal
      visible={visible}
      onCancel={onClose}
      width={1400}
      className="pv-map-modal"
      style={{ top: 24, paddingBottom: 0 }}
      title={modalTitle}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="save"
          type="primary"
          loading={saving}
          disabled={loading || Boolean(loadError) || !detail}
          onClick={handleSave}
        >
          Save mappings & special data
        </Button>,
      ]}
      destroyOnClose
    >
      <div className="pv-map-shell">
        {loading ? (
          <div className="pv-map-loading">
            <Spin size="large" />
            <Text>Loading partner documents…</Text>
          </div>
        ) : loadError ? (
          <div className="pv-map-loading">
            <Empty description={loadError} />
            <Button type="primary" onClick={loadDetail}>
              Retry
            </Button>
          </div>
        ) : (
          <>
            <div className="pv-map-banner">
              <Icon type="info-circle" className="pv-map-banner-icon" />
              <span>
                OCR suggestions are hints only — verify each file and serial number before saving.
              </span>
            </div>

            {detail?.ticket && (
              <div className="pv-map-meta">
                <Tag color="blue">{detail.ticket.project_name}</Tag>
                <Tag>{detail.notification?.unmapped_file_count || pendingFiles.length} file(s)</Tag>
                {detail.notification?.batch_id ? (
                  <Tag>Batch {String(detail.notification.batch_id).slice(0, 8)}</Tag>
                ) : null}
                {detail.notification?.visit_number != null ? (
                  <Tag>Visit {detail.notification.visit_number}</Tag>
                ) : null}
              </div>
            )}

            <div className="pv-map-grid">
              <section className="pv-map-panel">
                <div className="pv-map-panel-head">
                  <Title level={4} className="pv-map-panel-title">
                    Partner files
                  </Title>
                  <Text className="pv-map-panel-hint">
                    Drag onto a category, or select a file then click a category
                  </Text>
                </div>
                <div className="pv-map-panel-body">
                  {pendingFiles.length === 0 ? (
                    <Empty description="No files in this batch" />
                  ) : (
                    <div className="pv-map-file-list">
                      {pendingFiles.map((item) => (
                        <div
                          key={item.id}
                          draggable
                          onDragStart={() => setDragFileId(item.id)}
                          onClick={() => setPreviewFile(item)}
                          className={
                            previewFile?.id === item.id
                              ? "pv-map-file-card active"
                              : "pv-map-file-card"
                          }
                        >
                          {renderFileThumb(item)}
                          <div className="pv-map-file-main">
                            <div className="pv-map-file-name-row">
                              <Text className="pv-map-file-name">{item.original_name}</Text>
                              {assignments[item.id] ? (
                                <Tooltip title="Undo assignment">
                                  <Button
                                    type="link"
                                    size="small"
                                    className="pv-map-file-undo"
                                    onClick={(e) => clearAssignment(item.id, e)}
                                  >
                                    <Icon type="close-circle" />
                                  </Button>
                                </Tooltip>
                              ) : null}
                            </div>
                            <div className="pv-map-file-tags">
                              {item.detected_serial ? (
                                <Tag color={item.detected_unit === "unknown" ? "orange" : "blue"}>
                                  {item.detected_unit === "unknown"
                                    ? `Serial: ${item.detected_serial}`
                                    : `${item.detected_unit}: ${item.detected_serial}`}
                                </Tag>
                              ) : null}
                              {assignments[item.id] ? (
                                suggestedAssignments[item.id] === assignments[item.id] ? (
                                  <Tag color="orange">Suggested → {assignments[item.id]}</Tag>
                                ) : (
                                  <Tag color="green">→ {assignments[item.id]}</Tag>
                                )
                              ) : (
                                <Tag>Unassigned</Tag>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </section>

              <section className="pv-map-panel">
                <div className="pv-map-panel-head">
                  <Title level={4} className="pv-map-panel-title">
                    Document categories
                  </Title>
                  <Text className="pv-map-panel-hint">
                    Drop a file here or click while a file is selected
                  </Text>
                </div>
                <div className="pv-map-panel-body">
                  {categories.length === 0 ? (
                    <Empty description="No document categories on this ticket" />
                  ) : (
                    <div className="pv-map-categories">
                      {categories.map((cat) => {
                        const isAssigned = (categoryFiles[cat] || []).length > 0;
                        return (
                          <div
                            key={cat}
                            className={isAssigned ? "pv-map-category assigned" : "pv-map-category"}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={() => {
                              if (dragFileId) {
                                assignFile(dragFileId, cat);
                                setDragFileId(null);
                              }
                            }}
                            onClick={() => {
                              if (previewFile) assignFile(previewFile.id, cat);
                            }}
                          >
                            <span className="pv-map-category-name">{cat}</span>
                            {(categoryFiles[cat] || []).length > 0 ? (
                              <div className="pv-map-category-files">
                                {categoryFiles[cat].map((file) => (
                                  <span
                                    key={file.id}
                                    className="pv-map-category-file"
                                    title={file.name}
                                  >
                                    {file.name}
                                  </span>
                                ))}
                              </div>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {showSpecialData && (
                    <div className="pv-map-special-data">
                      <div className="pv-map-special-head">
                        <div className="pv-map-special-icon">
                          <Icon type="database" />
                        </div>
                        <div>
                          <Title level={4} className="pv-map-special-title">
                            Serial numbers
                          </Title>
                          <Text className="pv-map-panel-hint">
                            Confirm or edit values before saving to ticket special data
                          </Text>
                        </div>
                      </div>

                      <div className="pv-map-special-fields">
                        {specialDataKeys.map((key) => {
                          const suggested = ocrSuggestions[key];
                          const current = specialData[key] || "";
                          const isSuggested = Boolean(
                            suggested && current && current === suggested
                          );
                          return (
                            <div key={key} className="pv-map-special-field">
                              <div className="pv-map-special-field-top">
                                <label className="pv-map-special-label">{key}</label>
                                {isSuggested ? (
                                  <Tag color="orange" className="pv-map-special-tag">
                                    OCR suggested
                                  </Tag>
                                ) : null}
                              </div>
                              <Input
                                value={current}
                                placeholder={`Enter ${key}`}
                                onChange={(e) =>
                                  setSpecialData((prev) => ({
                                    ...prev,
                                    [key]: e.target.value,
                                  }))
                                }
                              />
                              {suggested && current !== suggested ? (
                                <Button
                                  type="link"
                                  size="small"
                                  className="pv-map-special-apply"
                                  onClick={() => applySerialToField(key, suggested)}
                                >
                                  Use OCR: {suggested}
                                </Button>
                              ) : null}
                            </div>
                          );
                        })}
                      </div>

                      {(detail?.unassigned_serials || []).length > 0 && (
                        <div className="pv-map-unassigned">
                          <Text className="pv-map-unassigned-label">
                            Unassigned OCR serials — click serial to preview, copy to clipboard
                          </Text>
                          <div className="pv-map-unassigned-tags">
                            {(detail.unassigned_serials || []).map((row) => (
                              <div key={row.file_id} className="pv-map-unassigned-item">
                                <Tag
                                  className="pv-map-unassigned-tag"
                                  onClick={() => {
                                    const f = pendingFiles.find((x) => x.id === row.file_id);
                                    if (f) setPreviewFile(f);
                                  }}
                                >
                                  {row.detected_serial}
                                </Tag>
                                <Tooltip title="Copy serial">
                                  <span className="pv-map-unassigned-copy-wrap">
                                    <Icon
                                      type="copy"
                                      className="pv-map-unassigned-copy"
                                      aria-label={`Copy ${row.detected_serial}`}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        copyTextToClipboard(row.detected_serial);
                                      }}
                                    />
                                  </span>
                                </Tooltip>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </section>

              <section className="pv-map-panel pv-map-panel-preview">
                <div className="pv-map-panel-head">
                  <Title level={4} className="pv-map-panel-title">
                    Preview
                  </Title>
                  <Text className="pv-map-panel-hint">
                    Scroll or slider to zoom · drag to pan · fullscreen for detail review
                  </Text>
                </div>
                <div className="pv-map-panel-body pv-map-panel-body-preview">
                  <DocumentPreviewViewer file={previewFile} />
                </div>
              </section>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

const PostVisitDocNotifications = () => {
  const userRole = localStorage.getItem("user_role") || "";
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [mappingId, setMappingId] = useState(null);
  const [reminderVisible, setReminderVisible] = useState(false);
  const [dismissingId, setDismissingId] = useState(null);

  const shouldShow = DOCUMENT_MAPPING_ROLES.has(userRole);

  const fetchNotifications = useCallback(async () => {
    if (!shouldShow) return;
    setLoading(true);
    try {
      const res = await getData("/management/post-visit-doc-notifications/?tab=pending");
      if (res?.data?.status === 200) {
        const rows = res.data.notifications || [];
        setNotifications(rows);
        setUnreadCount(res.data.unread_count || 0);
        const snoozeUntil = readSnoozeUntil();
        setReminderVisible(rows.length > 0 && Date.now() >= snoozeUntil);
      }
    } catch (error) {
      // silent poll failures
    } finally {
      setLoading(false);
    }
  }, [shouldShow]);

  const handleDismiss = useCallback(
    async (id) => {
      setDismissingId(id);
      try {
        const res = await patchData(`/management/post-visit-doc-notifications/${id}/`, {
          is_dismissed: true,
        });
        if (res?.data?.status === 200) {
          message.success("Upload request dismissed");
          fetchNotifications();
        } else {
          message.error(res?.data?.message || "Dismiss failed");
        }
      } catch (error) {
        message.error("Dismiss failed");
      } finally {
        setDismissingId(null);
      }
    },
    [fetchNotifications]
  );

  const openDrawer = useCallback(() => {
    snoozeReminder();
    setReminderVisible(false);
    setDrawerOpen(true);
  }, []);

  const dismissReminder = useCallback(() => {
    snoozeReminder();
    setReminderVisible(false);
  }, []);

  useEffect(() => {
    if (!shouldShow) return undefined;
    fetchNotifications();
    const timer = setInterval(fetchNotifications, POLL_MS);
    const onFocus = () => fetchNotifications();
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(timer);
      window.removeEventListener("focus", onFocus);
    };
  }, [fetchNotifications, shouldShow]);

  if (!shouldShow) return null;

  return (
    <>
      {reminderVisible && notifications.length > 0 ? (
        <div className="pv-notify-reminder">
          <Alert
            type="info"
            showIcon
            message={`${notifications.length} partner upload batch${notifications.length === 1 ? "" : "es"
              } waiting for document mapping`}
            description="Map files to ticket categories or dismiss requests you do not need to action."
            action={
              <div className="pv-notify-reminder-actions">
                <Button size="small" onClick={openDrawer}>
                  Review
                </Button>
                <Button size="small" onClick={dismissReminder}>
                  Remind later
                </Button>
                <Link to="/document-mapping">Open page</Link>
              </div>
            }
          />
        </div>
      ) : null}

      <button type="button" className="pv-notify-fab" onClick={openDrawer} aria-label="Partner document uploads">
        <span className="pv-notify-fab-glow" aria-hidden />
        <span className="pv-notify-fab-inner">
          <Icon type="file-done" className="pv-notify-fab-icon" />
          {unreadCount > 0 ? (
            <span className="pv-notify-fab-badge">{unreadCount > 99 ? "99+" : unreadCount}</span>
          ) : null}
        </span>
        <span className="pv-notify-fab-label">Doc mapping</span>
      </button>

      <Drawer
        placement="right"
        width={560}
        wrapClassName="pv-notify-drawer"
        closable={false}
        onClose={() => {
          snoozeReminder();
          setDrawerOpen(false);
        }}
        visible={drawerOpen}
        title={
          <div className="pv-notify-drawer-header-row">
            <div className="pv-notify-drawer-title">
              <span className="pv-notify-drawer-title-icon">
                <Icon type="file-done" />
              </span>
              <div className="pv-notify-drawer-title-text">
                <span className="pv-notify-drawer-title-main">Partner document uploads</span>
                <span className="pv-notify-drawer-title-sub">Pending post-visit batches</span>
              </div>
            </div>
            <div className="pv-notify-drawer-header-actions">
              {notifications.length > 0 ? (
                <span className="pv-notify-drawer-title-count">{notifications.length}</span>
              ) : null}
              <button
                type="button"
                className="pv-notify-drawer-close"
                aria-label="Close"
                onClick={() => {
                  snoozeReminder();
                  setDrawerOpen(false);
                }}
              >
                <Icon type="close" />
              </button>
            </div>
          </div>
        }
      >
        <div className="pv-notify-drawer-shell">
          <div className="pv-notify-drawer-toolbar">
            <Text className="pv-notify-drawer-toolbar-label">
              Action needed before files can attach to tickets
            </Text>
            <Link
              to="/document-mapping"
              className="pv-notify-drawer-page-link"
              onClick={() => {
                snoozeReminder();
                setDrawerOpen(false);
              }}
            >
              Full queue
              <Icon type="arrow-right" />
            </Link>
          </div>

          {loading ? (
            <div className="pv-notify-drawer-loading">
              <Spin />
              <Text type="secondary">Loading upload queue…</Text>
            </div>
          ) : notifications.length === 0 ? (
            <div className="pv-notify-drawer-empty">
              <Icon type="check-circle" theme="filled" className="pv-notify-drawer-empty-icon" />
              <Text strong className="pv-notify-drawer-empty-title">
                Queue clear
              </Text>
              <Text type="secondary">No partner uploads waiting for mapping right now.</Text>
            </div>
          ) : (
            <div className="pv-notify-queue">
              {notifications.map((item) => (
                <article
                  key={item.id}
                  className={
                    item.is_read ? "pv-notify-card" : "pv-notify-card pv-notify-card--unread"
                  }
                >
                  <div className="pv-notify-card-top">
                    <div className="pv-notify-card-id">{item.cti || item.ticket_number}</div>
                    <time className="pv-notify-card-time">{moment(item.created).fromNow()}</time>
                  </div>
                  <div className="pv-notify-card-partner">{item.partner_name || "Partner"}</div>
                  <div className="pv-notify-card-meta">
                    <span className="pv-notify-card-files">
                      <Icon type="paper-clip" />
                      {item.unmapped_file_count} file{item.unmapped_file_count === 1 ? "" : "s"}
                    </span>
                    {/* {item.batch_id ? ( // Not really importantn here in drawer, its fine in other places
                      <span className="pv-notify-card-batch">
                        Batch {String(item.batch_id).slice(0, 8)}
                      </span>
                    ) : null} */}
                    {item.visit_number != null ? (
                      <span className="pv-notify-card-visit">Visit {item.visit_number}</span>
                    ) : null}
                    {!item.is_read ? <span className="pv-notify-card-new">New</span> : null}
                  </div>
                  <div className="pv-notify-card-actions">
                    <Button
                      type="primary"
                      size="small"
                      className="pv-notify-card-map"
                      onClick={() => {
                        setMappingId(item.id);
                        setDrawerOpen(false);
                      }}
                    >
                      Map documents
                    </Button>
                    <Button
                      type="link"
                      size="small"
                      className="pv-notify-card-dismiss"
                      loading={dismissingId === item.id}
                      onClick={() => handleDismiss(item.id)}
                    >
                      Dismiss
                    </Button>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </Drawer>

      <PostVisitDocMappingModal
        visible={Boolean(mappingId)}
        notificationId={mappingId}
        onClose={() => setMappingId(null)}
        onSaved={fetchNotifications}
      />
    </>
  );
};

export { PostVisitDocMappingModal };
export default PostVisitDocNotifications;
